import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ChapterDetailModal, { DonutArc } from "./ChapterDetailModal";

function Chapters({ apiBase, token, onPageBusyChange }) {
  const [chapters, setChapters] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [modalChapterStats, setModalChapterStats] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  async function openModal(chapter) {
    setSelectedChapter(chapter);
    setModalChapterStats(null);
    setModalLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/mastery/chapters/${chapter.id}/frames`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error("Failed to load chapter frames");
      const payload = await res.json();
      setModalChapterStats({
        ...(payload.chapterStats || {}),
        totalFrames: payload.totalFrames || 0,
        masteredFrames: payload.masteredFrames || 0,
        recommendedNext: payload.recommendedNext || null
      });
    } catch (err) {
      setModalChapterStats(null);
    } finally {
      setModalLoading(false);
    }
  }

  function handleResume(nextFrame) {
    setSelectedChapter(null);
    if (nextFrame) {
      const params = new URLSearchParams({
        difficulty: nextFrame.difficulty,
        frameOrder: String(nextFrame.frameOrder ?? ""),
      });
      navigate(`/practice/${selectedChapter.id}?${params.toString()}`);
    } else {
      navigate(`/practice/${selectedChapter.id}`);
    }
  }

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("chapters", loading, "Loading chapters...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("chapters", false);
      }
    };
  }, [loading, onPageBusyChange]);

  useEffect(() => {
    let isMounted = true;

    async function loadChapters() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${apiBase}/api/chapters`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load chapters");
        }

        const payload = await response.json();
        const items = payload.chapters || [];

        const normalized = items.map((item) => ({
          id: item.id || item.chapterId || item._id,
          title: item.title || item._id,
          grade: item.grade || item.level || "Grade 9",
          level: item.grade || item.level || "Grade 9",
          description: item.description || "",
          totalFrames: item.totalFrames || 0,
          masteredFrames: item.masteredFrames || 0,
          overallStatus: item.overallStatus || "not_started"
        }));

        if (isMounted) {
          setChapters(normalized);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Failed to load chapters");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    if (token) {
      loadChapters();
    }

    return () => {
      isMounted = false;
    };
  }, [apiBase, token]);

  const gradeOptions = useMemo(() => {
    const grades = [...new Set(chapters.map((chapter) => chapter.grade).filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );
    return [{ value: "all", label: "All Chapters" }, ...grades.map((grade) => ({ value: grade, label: grade }))];
  }, [chapters]);

  const visibleChapters = useMemo(() => {
    if (selectedGrade === "all") return chapters;
    return chapters.filter((chapter) => chapter.grade === selectedGrade);
  }, [chapters, selectedGrade]);

  return (
    <section className="page">
      <h2 className="page-title">Chapters</h2>
      <p className="page-sub">
        Choose a chapter to start your Grade 9/10 math questionnaire.
      </p>

      {!loading && !error && chapters.length > 0 && (
        <div className="filters-panel chapter-grade-filter-panel">
          <div className="filter-group">
            <div className="filter-label">Grade</div>
            <div className="filter-row">
              {gradeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`filter-pill ${selectedGrade === option.value ? "active" : ""}`}
                  onClick={() => setSelectedGrade(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="page-sub">Loading chapters...</div>}
      {error && <div className="error-text">{error}</div>}

      {!loading && !error && (
        <div className="card-grid">
          {visibleChapters.map((chapter) => (
            <div
              key={chapter.id}
              className="chapter-card"
              role="button"
              tabIndex={0}
              onClick={() => openModal(chapter)}
              onKeyDown={(e) => e.key === "Enter" && openModal(chapter)}
              style={{ cursor: "pointer" }}
            >
              {/* Left Info: Title & Mastery Text */}
              <div className="chapter-card-info">
                <div className="chapter-card-meta-row">
                  <span className="chapter-grade-badge">{chapter.grade || "Grade 9"}</span>
                </div>
                <div className="chapter-title">{chapter.title}</div>
                <div className="chapter-sub">
                  {chapter.masteredFrames || 0} / {chapter.totalFrames || 0} Frames Mastered
                </div>
              </div>
              {/* Right Side: Scaled Donut Arc (88px) */}
              <div className="chapter-card-donut">
                <DonutArc
                  masteredFrames={chapter.masteredFrames || 0}
                  totalFrames={chapter.totalFrames || 0}
                  overallStatus={chapter.overallStatus || "not_started"}
                  size={88}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && chapters.length > 0 && visibleChapters.length === 0 && (
        <div className="question-meta">No chapters found for this grade yet.</div>
      )}

      {selectedChapter && (
        <ChapterDetailModal
          chapter={selectedChapter}
          chapterStats={modalChapterStats}
          loading={modalLoading}
          onClose={() => setSelectedChapter(null)}
          onResume={handleResume}
          onExplore={() => {
            setSelectedChapter(null);
            navigate(`/chapters/${selectedChapter.id}`);
          }}
        />
      )}
    </section>
  );
}

export default Chapters;
